import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import { Grid, Slide } from '@material-ui/core';

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend, Filler,
    ChartEvent,
    ActiveElement,
    ScriptableContext
} from 'chart.js';
import { connect } from "react-redux";
import { ApplicationState } from "../../store";
import * as APIStore from "../../store/APIStore";
import RequestAccessToken from '../../util/authService';
import BarChartIcon from '@material-ui/icons/BarChart';
import { shouldTryRefMDProfile, shouldTryCareCoordinatorProfile } from '../common/ProfileHelper';
import { getMonthName } from '../../util/DateHelper';

interface AdmitDataPoint {
    x: string;
    y: number;
    meta: {
        month: number;
        year: number;
    };
}

type OwnProps = {
    openDrillDownModal: (month: number, year: number) => void;
};

const mapState = (state: ApplicationState) => state.vitalsContext;

type StateProps = ReturnType<typeof mapState>;
type DispatchProps = typeof APIStore.actionCreators;
type AcutePatientsByMonthProps = OwnProps & StateProps & DispatchProps;

const AcutePatientsByMonth = (props: AcutePatientsByMonthProps) => {

    let {
        PhysicianAdmitsStats,
        requestVitalsMdProfile,
        userPracticeCoordinatorProfile,
        userRefMdProfile,
        APIToken,
        requestCurrentUserCareCoordinatorProfile,
        requestAdmitStatsForProvider,
        receiveTokenResponse,
        openDrillDownModal
    } = props;

    let [tokenState, setTokenState] = useState<APIStore.TokenType>();
    let PhysicianAdmits = {};

    if (PhysicianAdmitsStats) {
        PhysicianAdmits = PhysicianAdmitsStats;
    }


    RequestAccessToken()
        .then((token) => {
            if (token && !tokenState) {
                setTokenState(token);
                receiveTokenResponse(token);
            }
        });

    useEffect(() => {
        if (APIToken && !tokenState?.accessToken) {
            setTokenState(APIToken);
        }

    }, [APIToken, tokenState])

    useEffect(() => {
        if (shouldTryRefMDProfile(userRefMdProfile) && tokenState?.accessToken && userPracticeCoordinatorProfile.notFound) {
            requestVitalsMdProfile(tokenState.accessToken);
        }

    }, [tokenState, userRefMdProfile, userPracticeCoordinatorProfile])

    useEffect(() => {
        if (shouldTryCareCoordinatorProfile(userPracticeCoordinatorProfile) && tokenState?.accessToken) {
            requestCurrentUserCareCoordinatorProfile(tokenState.accessToken);
        }
    }, [tokenState, userRefMdProfile, userPracticeCoordinatorProfile]);

    useEffect(() => {
        if (tokenState?.accessToken && !PhysicianAdmitsStats.isLoaded && !PhysicianAdmitsStats.isLoading) {
            requestAdmitStatsForProvider(tokenState.accessToken);
        }

    }, [tokenState, userRefMdProfile, userPracticeCoordinatorProfile]);


    if (!PhysicianAdmitsStats || PhysicianAdmitsStats.isLoading || !PhysicianAdmitsStats.isLoaded) {
        return (
            <Slide in timeout={1000} direction="right">
                <Grid container>
                    <Grid item md={4}>
                        <BarChartIcon className="animate-flicker" style={{ fontSize: 100 }} color="disabled"></BarChartIcon>
                    </Grid>
                </Grid>
            </Slide>
        )
    }

    if (PhysicianAdmitsStats.isErrored) {
        return (<React.Fragment><p>Error Loading</p></React.Fragment>)
    }

    ChartJS.register(
        CategoryScale,
        LinearScale,
        PointElement,
        LineElement,
        Title,
        Tooltip,
        Legend, Filler
    );
    const options = {
        responsive: true,
        plugins: {
            legend: {
                position: "top" as const,
            },
            title: {
                display: true,
                text: "All Patients",
            },
        },
        onClick: (_event: ChartEvent, elements: ActiveElement[], chart: ChartJS) => {
            if (!elements.length) return;

            const { datasetIndex, index } = elements[0];
            const point = chart.data.datasets[datasetIndex].data[index] as unknown as AdmitDataPoint;

            openDrillDownModal(point.meta.month, point.meta.year);
        },
        onHover: (event: ChartEvent, elements: ActiveElement[]) => {
            const target = event?.native?.target as HTMLElement;
            if (target) {
                target.style.cursor = elements.length ? "pointer" : "default";
            }
        }
    };

    let width: number, height: number, gradient: CanvasGradient;
    function getBlueGradient(ctx: any, chartArea: any) {
        const chartWidth = chartArea.right - chartArea.left;
        const chartHeight = chartArea.bottom - chartArea.top;
        if (!gradient || width !== chartWidth || height !== chartHeight) {
            // Create the gradient because this is either the first render
            // or the size of the chart has changed
            width = chartWidth;
            height = chartHeight;
            gradient = ctx.createLinearGradient(
                0,
                chartArea.bottom,
                0,
                chartArea.top
            );

            gradient.addColorStop(0, "rgba(82, 189, 228, 0.05)");
            gradient.addColorStop(0.5, "rgba(82, 189, 228, 0.1)");
            gradient.addColorStop(1, "rgba(82, 189, 228, 0.2)");
        }

        return gradient;
    }


    let values: AdmitDataPoint[] = PhysicianAdmitsStats.physicianAdmits.map((val: APIStore.AdmitSummaryStatistics) => {
        return {
            x: `${getMonthName(val.month)}-${val.year}`,
            y: val.totalAdmits,
            meta: {
                month: val.month,
                year: val.year
            }
        }
    });


    const data = {
        datasets: [
            {
                label: "Admits",
                data: values,
                borderColor: "rgba(82, 189, 228, 1)",
                backgroundColor: function (context: ScriptableContext<'line'>) {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;

                    if (!chartArea) {
                        // This case happens on initial chart load
                        return;
                    }
                    return getBlueGradient(ctx, chartArea);
                },
                pointRadius: 4,
                pointHoverRadius: 7,
                pointHitRadius: 10,

                fill: true,
            },
        ],
    };

    return (
        <React.Fragment>
            <Grid item xs={12} direction="column" container>
                <Grid item xs={12}>
                    <Line
                        options={options}
                        data={data}
                    />
                </Grid>

            </Grid>


        </React.Fragment>
    );
};

export default connect<StateProps, DispatchProps, OwnProps, ApplicationState>(
    (state: ApplicationState) => state.vitalsContext,
    APIStore.actionCreators
)(AcutePatientsByMonth as any);
